export const AGENTS_MD_VERSION = 18;

export const MOMENT_MANAGED_SECTION_START = "<!-- MOMENT_MANAGED_SECTION_START -->";
export const MOMENT_MANAGED_SECTION_END = "<!-- MOMENT_MANAGED_SECTION_END -->";

export const AGENTS_MD_TEMPLATE = `<section readonly data-variant="moment-agents-template">
<!-- MOMENT_AGENTS_MD_VERSION:${AGENTS_MD_VERSION} -->
${MOMENT_MANAGED_SECTION_START}

# Agent Instructions for Moment Documents

## Repository layout

-   \`moment.yml\`: Document metadata file at the root of every Moment document. Contains:

    -   \`id\`: Unique document identifier
    -   \`title\`: Document title
    -   \`pagesMetadata\`: Array of page entries, each with:
        -   \`id\`: Unique page ID
        -   \`slug\`: URL slug for routing (e.g., "getting-started")
        -   \`title\`: Page title
        -   \`path\`: (optional) Path to the page file, relative to document root. When present,
            this is the on-disk path the runtime should use for the page.
        -   \`parentPageID\`: (optional) Parent page ID for nested page hierarchies
        -   \`type\`: (optional) Page type - \`markdown\`, \`canvas\`, or \`request\`. \`request\` pages
            provide an interface for building HTTP, Postgres, SQLite, gRPC, and JavaScript
            requests, and can be invoked by JavaScript code cells (more on this later).

-   \`.moment/attachments/\`: Directory for file attachments (CSVs, SQLite databases, and other
    data files). Files are added here via drag-and-drop or the attachment picker in the navigation
    pane. Attachments are tracked in version control so they travel with the document.
    Attachments can be queried using SQLite \`request\` pages or read directly from JavaScript
    code cells.

-   \`pages/\`: Common location for page files. When a page has an explicit \`path\`, it may use a
    slug-based hierarchy such as \`pages/docs/getting-started/index.md\`. When \`path\` is omitted,
    parts of the current runtime still fall back to page-ID-based locations like
    \`pages/{pageId}/index.md\` for markdown pages and \`pages/{pageId}/index.yml\` for
    \`request\` pages.

## Finding a page on disk

1. Look up the page in \`moment.yml\` → \`pagesMetadata\` by its \`id\` or \`slug\`.
2. If the page has a \`path\` field, the file is at that path relative to the document root.
3. If the page has no \`path\`, be aware that parts of the current runtime still fall back to
   page-ID-based paths:

    -   the file for \`markdown\` pages may be read from or written to \`pages/{pageId}/index.md\`.
    -   the file for \`request\` pages may be read from or written to \`pages/{pageId}/index.yml\`.
4. Only rely on slug-based paths like \`pages/{full-slug-path}/index.md\` or
   \`pages/{full-slug-path}/index.yml\` when the page metadata explicitly includes that \`path\`.
5. For nested pages, \`full-slug-path\` means joining the current page slug with all ancestor slugs,
   such as \`parent/child/grandchild\`.
6. When resolving the document's root page, a top-level page with slug \`index\` takes priority.
   If no top-level page has slug \`index\`, the first top-level page in the document is treated as
   the index page.

## Instructions

-   Use standard Markdown syntax when writing Markdown pages.
-   To insert an empty line, use \`<br />\`.
-   Other than using \`<br />\` to represent empty lines, **DO NOT USE HTML SYNTAX WHEN WRITING
    MARKDOWN PAGES.** This is very important, as we do not support general-purpose HTML in Markdown
    pages.
-   **DO NOT DIRECTLY USE MDX SYNTAX WHEN WRITING MARKDOWN PAGES.** This is also very important, as
    we do not support MDX, either.
-   **VERY IMPORTANT:** Prefer wiki-style internal page links like \`[[Page Title]]\`.
-   For nested pages, use slash-separated page references like \`[[Parent/Child]]\`.
-   For links to headings or fragments, use \`[[Page Title#fragment]]\` or
    \`[[Parent/Child#fragment]]\`.
-   For custom display text, use \`[[Page Title|Alias]]\` or \`[[Parent/Child#fragment|Alias]]\`.
-   Avoid raw \`/d/...\` internal links unless there is a specific reason to use one.
-   External links: \\\`[text](https://example.com)\\\`
-   To insert a TODO list item, use \\\`[ ]\\\` (incomplete) or \\\`[x]\\\` (complete).
-   We accept all the usual Markdown syntax for lists, including \`*\`, \`-\`, and \`+\` for unordered
    lists, and \`1.\`, \`2.\`, \`3.\`, etc. for ordered lists.
-   List types may be freely mixed and nested at any level.
-   **DO NOT INDENT LIST ITEMS MORE THAN 8 LEVELS.**
-   We also support all the other well-trafficked Markdown syntax: fenced code blocks (triple
    backticks), inline code blocks (backticks), headings (H1, H2, H3, H4, H5, H6, via either \`# \`
    syntax or \`===\` or \`---\` syntax), blockquotes (\`> \`), bold (\`**\`, \`__\`), italic (\`*\`,
    \`_\`), strikethrough (\`~~\`), links (\`[text](https://example.com)\`), images
    (\`![text](https://example.com)\`), and horizontal rules (\`---\`, \`***\`, \`___\`).
-   We also support Mermaid diagrams, via the \\\`mermaid\\\` function in JavaScript code cells. See
    below for more details.
-   **VERY IMPORTANT: DO NOT ADD IMAGES.** Let the humans add images if they like.
-   When creating a new page, keep the \`title\` human-readable. The page \`slug\` and default
    directory name should be sluggified versions of the title, and sibling slug collisions should
    be resolved with numeric suffixes like \`-2\`.

## Adding JavaScript code cells

JavaScript code cells a special fenced code blocks that are executed by the Moment runtime. They
allow you to build interactive documents that fetch live data, compute derived values, render
dynamic visualizations, and respond to user input.

-   **VERY IMPORTANT:** If you use React or HTML in a Javascript code cell, you **MUST** use
    Tailwind CSS classes for styling. DO NOT USE INLINE STYLING.
-   **VERY IMPORTANT:** ALWAYS use \`React.useState\`, \`React.useEffect\`, \`React.useCallback\`,
    \`React.useMemo\`, \`React.useRef\`, etc. NEVER use the raw \`useState\`, \`useEffect\`, etc.
    directly. React hooks are only available as \`React.*\` in code cells.
-   **VERY IMPORTANT:** ALWAYS design UIs that work in BOTH light mode and dark mode. Use the
    \`dark:\` variant to provide dark-mode overrides. For example:
    \`bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-700\`.
    NEVER produce UIs that only work in one mode — every background, text, and border color should
    have a corresponding \`dark:\` variant.
-   **VERY IMPORTANT:** Only use Tailwind color classes from the following safelist. DO NOT use
    colors outside this set:
    -   Utilities: \`bg-\`, \`text-\`, \`border-\`, \`ring-\`, \`divide-\`
    -   Colors: \`gray\`, \`red\`, \`orange\`, \`amber\`, \`yellow\`, \`lime\`, \`green\`, \`emerald\`,
        \`teal\`, \`cyan\`, \`sky\`, \`blue\`, \`indigo\`, \`violet\`, \`purple\`, \`fuchsia\`, \`pink\`,
        \`rose\`
    -   Shades: \`50\`, \`100\`, \`200\`, \`300\`, \`400\`, \`500\`, \`600\`, \`700\`, \`800\`, \`900\`,
        \`950\`
    -   Variants: \`hover\`, \`focus\`, \`dark\`, \`dark:hover\`, \`dark:focus\`
    -   For example: \`bg-gray-800\`, \`text-blue-400\`, \`hover:bg-gray-700\`, \`border-gray-600\`
    -   Colors like \`slate\`, \`zinc\`, \`neutral\`, \`stone\` are NOT available.
-   **VERY IMPORTANT:** NEVER define a React component in the same cell as a \`render()\` call.
    Components MUST be defined in a SEPARATE cell and referenced by their \`cellName\`. Defining
    a component inline in \`render()\` or even in the same cell will NOT work — hooks will crash,
    and the component will not re-render correctly.

    WRONG (will crash):

    \`\`\`javascript
    // Cell: counterView
    return render(() => {
        const [count, setCount] = React.useState(0);
        return <button onClick={() => setCount(c => c + 1)}>{count}</button>;
    });
    \`\`\`

    ALSO WRONG (same cell):

    \`\`\`javascript
    // Cell: counterView
    function Counter() {
        const [count, setCount] = React.useState(0);
        return <button onClick={() => setCount(c => c + 1)}>{count}</button>;
    }
    return render(() => <Counter />);
    \`\`\`

    CORRECT (two separate cells):

    \`\`\`javascript
    // Cell: Counter
    function Counter() {
        const [count, setCount] = React.useState(0);
        return <button onClick={() => setCount(c => c + 1)}>{count}</button>;
    }
    return Counter;
    \`\`\`

    \`\`\`javascript
    // Cell: counterView
    return render(() => <Counter />);
    \`\`\`

-   **VERY IMPORTANT:** Avoid using the Tailwind \`group\` class. Code cells are rendered inside
    parent elements that already use \`group\`, so your \`group-hover:\` etc. utilities will trigger
    unexpectedly. We do not JIT-compile Tailwind, so \`group/name\` (named groups) will NOT work
    either. Use explicit state (e.g., \`React.useState\` with \`onMouseEnter\`/\`onMouseLeave\`) instead.
-   **VERY IMPORTANT:** Ordering of compute cells does not matter! It is helpful and desirable to
    put cells that render important data and UI at the top, and cells that do intermediate
    computation or data fetching towards the bottom, usually in a section at the end denoted by
    \`# Appendix\`.

For example, here is a JavaScript code cell that fetches the last 100 pull requests from the
\`octocat/hello-world\` repository:

\`\`\`JavaScript cellId:Ox6wWLnoyjiHZAYLJcXss2Pg93xOEpyhxt2n7bJMlbPD2Uoj cellName:prs
const { Octokit } = await import('https://cdn.skypack.dev/@octokit/core@3.5.1?min');

const octokit = new Octokit({});
return await octokit.request('GET /repos/{owner}/{repo}/pulls', {
    owner: "octocat",
    repo: "hello-world",
    per_page: 100,
    state: "all",
});
\`\`\`

Here is another, which renders a Mermaid sequence diagram:

\`\`\`JavaScript cellId:fkpnfAZjNnSvD3y17zavarytiT8csBjan5ZL7yqK5snerZUm cellName:sequenceDiagram
return mermaid\`sequenceDiagram
    Alice->John: Hello John, how are you?
    loop Every minute
        John-->Alice: Great!
    end
\`
\`\`\`

Here is a cell which fetches a JSON file from the internet and returns the result:

\`\`\`JavaScript cellId:cKIKYyw3Rx8F3i3qHMFJeSJ2W6Mzq0hOij70GnXBjhjXjvHL cellName:httpGet
const resp = await fetch("https://raw.githubusercontent.com/moment-eng/staticjson/main/raccoon.json");
return await resp.json();

\`\`\`

Here is a cell which uses the built-in \`html\` function to render HTML:

\`\`\`JavaScript cellId:nIDk4guakTvl5R9J434MXUQRZx4RHz52QUhtJiDtTWPpdzN4 cellName:simpleHtml
 return html\`<div class="border">Hello, world!</div>\`
\`\`\`

And here is one that renders a React component. **Always define the component in a separate cell
and reference it by \`cellName\` in the \`render()\` call. NEVER define a component in the same cell
as the \`render()\` call — it will not work.**

\`\`\`JavaScript cellId:kR3mT7nL2yPcQjV8dFbHs6WgZeNiU0XoCpMtYlS4vJ1Aw5K9x cellName:Greeting
function Greeting() {
    return <div className="border">Hello, world!</div>;
}
return Greeting;
\`\`\`

\`\`\`JavaScript cellId:bVZVu194a34S63yjihuN5CtpafGzHt5GZmWH8Sa8ju9Kes0E cellName:greetingView
return render(() => <Greeting />);
\`\`\`

Here is a cell that users \`yield\` to produce an infinite stream of numbers, at a rate of one number
per second:

\`\`\`JavaScript cellId:oxb5HGT5mhOpBUcM0hs40cdOrKyWqDLuzAUQBrwgrry6hVQF cellName:code
let i = 0;
while (true) {
    yield i++;
    await new Promise((resolve) => setTimeout(resolve, 1000));
}
\`\`\`

You can also create fetch calls in JavaScript cells with env var substitution.
For example, here is a cell that compares the \`v0.3.667\` tag with the \`main\` branch of the \`moment-eng/moment\` repository:

\`\`\`JavaScript cellId:oxbdddsT5mhOpBUcM0hs40cdOrKyWqDLuzAUQBrwgrry6hVQF cellName:compareTags
const resp = await fetch("https://api.github.com/repos/moment-eng/moment/compare/v0.3.667...main", {
  headers: {
    "Accept": "application/vnd.github.v3+json",
    "Authorization": \`Bearer {{env.GITHUB_TOKEN}}\`,
  },
});
return await resp.json();
\`\`\`

## IMPORTANT: Executing \`request\` pages from a JavaScript cell

If a document has pages with \`type: request\`, they can be executed inside a JavaScript cell. If you
have a \`moment.yml\` like this:

\`\`\`
id: 1zkalx4hzs8pn36n0hwd1z6ef
schemaVersion: 1
title: AWS stuff
created: 2026-01-13T17:06:53.365Z
updated: 2026-01-23T19:54:39.156Z
pagesMetadata:
  - id: 7m5g158avpd14y6xnl1zt3qcy
    slug: 7m5g158avpd14y6xnl1zt3qcy
    title: myPrs
    type: request
    archived: false
    parentPageID: null
\`\`\`

Then you can execute the request as so, with \`request\` taking the page's \`title\` (in this case
\`myPrs\`), and the \`key\` being any unique string you want to make up (this allows us to de-duplicate
identical queries, so any string is ok, you should consider using the \`title\` for both). You should
try to make the \`cellName\` similar to the title (but still unique in the document).

\`\`\`JavaScript cellId:iHCLhNLD7FGr7MQ9eAWYsGn6i6CgZ5CWONa0OMeqcLQYXSTx cellName:myPrs
return query({ request: "myPrs", key: "myPrs" }).stream()
\`\`\`

The return type is almost identical to TanStack Query:

\`\`\`typescript
export type HttpResponse = {
	kind: "http";
	status: number;
	statusText: string;
	ok: boolean;
	url: string;
	headers: [string, string][];
	parsedBody: unknown;
	bodyEncoding: "utf-8" | "base64";
	bodySize: number;
};

export type PostgresResponse = {
	kind: "postgres";
	columns: { name: string; dataType: string }[];
	rows: Record<string, unknown>[];
	rowCount: number;
	command: string;
	durationMs: number;
};

export type JavaScriptResponse = {
	kind: "javascript";
	parsedBody: unknown;
	bodySize: number;
	durationMs: number;
};

export type GrpcResponseMessage = {
	index: number;
	body: unknown;
	bodySize: number;
};

export type GrpcResponse = {
	kind: "grpc";
	/** gRPC status code (0 = OK) */
	statusCode: number;
	/** gRPC status name, e.g. "OK", "NOT_FOUND", "INTERNAL" */
	statusMessage: string;
	/** Response messages (1 for unary, N for streaming) */
	messages: GrpcResponseMessage[];
	/** Response metadata (headers) from the server */
	responseMetadata: [string, string][];
	/** Trailing metadata from the server */
	trailers: [string, string][];
	durationMs: number;
	/** Type of gRPC call that produced this response */
	streamType: "unary" | "server" | "client" | "bidi";
};

export type QueryResponse = HttpResponse | PostgresResponse | JavaScriptResponse | GrpcResponse;

export type QueryResult = {
	// Core data
	data: QueryResponse | undefined;
	error: QueryError | undefined;

	// Status enums (like TanStack Query)
	status: QueryStatus;
	fetchStatus: QueryFetchStatus;

	// Derived booleans for convenience
	isPending: boolean; // status === 'pending'
	isSuccess: boolean; // status === 'success'
	isError: boolean; // status === 'error' (and no cached data)
	isLoading: boolean; // isFetching && isPending (first load, no data)
	isFetching: boolean; // fetchStatus === 'fetching'
	isRefetching: boolean; // isFetching && !isPending (have data, refreshing)
	isStale: boolean; // have data but last fetch failed

	// Timestamps
	dataUpdatedAt: number | undefined; // When data was last successfully fetched
	errorUpdatedAt: number | undefined; // When the last error occurred

	// Timing (from last successful fetch)
	timing: QueryTiming | undefined;

	// Action to refetch the query
	refetch: () => Promise<QueryResult<T>>;
};
\`\`\`

When consuming a query result in another cell, always branch on \`data?.kind\` before reading
HTTP-specific or Postgres-specific fields.

\`\`\`javascript
// \`myPrs\` is the \`cellName\` of the cell above.
const { data, isError } = myPrs;

if (isError) {
    return null;
}

if (data?.kind === "http") {
    // For an HTTP request page, this is often the value you want.
    return data.parsedBody;
}

if (data?.kind === "postgres") {
    return data.rows;
}

if (data?.kind === "javascript") {
    return data.parsedBody;
}

if (data?.kind === "grpc") {
    // For unary calls, there is one message. For streaming, there are N messages.
    // Each message has a \`body\` (the parsed JSON response) and \`bodySize\`.
    return data.messages.map(m => m.body);
}

return null;
\`\`\`

### IMPORTANT: Refetching a query

Sometimes, the user will want to trigger a refetch of a query. You can do this by calling the
\`refetch\` function on the query result, exactly as you would for TanStack Query. For example:

\`\`\`JavaScript cellId:RZYYXN7cJiUxKqAxgF6dd4dh2yQYHVwlD91Im7B9pFoQc2ag cellName:moment_eng_prs
return query({ request: "moment_eng_prs", key: "GpSRA0Ocyf1iRc2WRK4Yr" }).stream()
\`\`\`

\`\`\`JavaScript cellId:DlD3KjOK7uXxq9J6bQfRsQXlHxjaTnCE2wYvbx7b0M3QXH56 cellName:RefreshButton
function RefreshButton() {
    return (
        <button
            className="rounded bg-blue-600 px-2 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            onClick={() => moment_eng_prs.refetch()}
            type="button"
        >
            Refresh moment_eng_prs
        </button>
    );
}
return RefreshButton;
\`\`\`

\`\`\`JavaScript cellId:xK7cJiUxKqAxgF6dd4dh2yQYHVwlD91Im7B9pFoQc2agRZYY cellName:refreshButtonView
return render(() => <RefreshButton />);
\`\`\`

### IMPORTANT: Fetching a query a single time

Sometimes, the user will want to fetch a query a single time, and not have it refetch automatically,
or have it stream the result back. You can do this by calling the \`fetch\` function. This will
return a \`Promise<QueryResponse>\`, and is appropriate for use in (*e.g.*) \`onClick\` handlers.

\`\`\`JavaScript cellId:jriOzN5gjcGKH5d3tewKC5fwoeAND0ZinRmyPZHdny9Dx13X cellName:moment_eng_prs0
return query({ request: "moment_eng_prs", key: "moment_eng_prs" }).fetch()
\`\`\`

### IMPORTANT: Template variable substitution with \`{{vars.X}}\`

Use \`{{vars.X}}\` in request pages to substitute caller-supplied values from \`query()\` or
\`mutation()\`. This works in HTTP, Postgres, and gRPC request pages:

-   In **HTTP** request pages: use \`{{vars.X}}\` in the URL, headers, body, or auth fields. The
    **Variables tab** in the request page UI lets you define default values for each variable.
    Store the variable name and its default value there as a key-value pair, and set it in the
    \`vars:\` Kv[] field in the YAML. The \`params:\` field is for URL query-string parameters only
    — do not use \`{{params.X}}\` in HTTP pages.
-   In **Postgres** request pages: use \`{{vars.X}}\` in the SQL or connection string. The
    **Variables tab** lets you define defaults in the \`params:\` Kv[] field.
-   In **gRPC** request pages: use \`{{vars.X}}\` in the message body, URL, service, method,
    or metadata values. The **Variables tab** in the request page UI lets you define default values
    for each variable. Store the variable name and its default value in the \`vars:\` Kv[] field.

Pass values from code cells via \`query({ ..., vars: { key: "value" } })\` or
\`mutateAsync({ key: "value" })\`. Caller-supplied values override the defaults defined in the
Variables tab.

\`{{env.X}}\` is the namespace for environment variables and secrets, and is separate from \`{{vars.X}}\`.

### IMPORTANT: Executing mutations with \`mutation()\`

The \`mutation()\` API is used for executing mutations against HTTP request pages (POST, PUT, PATCH,
DELETE), Postgres request pages (INSERT, UPDATE, DELETE), or gRPC request pages. It is modeled after TanStack Query\\'s
\`useMutation\`.

**Key differences from \`query()\`:**

-   \`mutation()\` does not cache results or deduplicate — each call executes independently.
-   \`mutation()\` supports an \`onSettled\` callback for invalidating queries after the mutation.
-   \`mutation()\` returns a \`MutationHandle\` with a \`mutateAsync()\` method.
-   \`mutateAsync({ key: "value" })\` substitutes \`{{vars.key}}\` placeholders in the request page.
    Define the body/URL template in the request page and pass the dynamic values to \`mutateAsync\`.

To use \`mutation()\`, first create a \`request\` page. For HTTP, define the body with \`{{vars.X}}\`
placeholders for the dynamic parts:

\`\`\`yaml
schemaVersion: 1
kind: Page
pageType: request
request:
  type: http
  method: POST
  url: https://api.example.com/users
  params: []
  vars:
    - enabled: true
      key: name
      value: ""
    - enabled: true
      key: email
      value: ""
  headers:
    - enabled: true
      key: Accept
      value: application/json
  auth:
    type: bearer
    token: "{{env.API_TOKEN}}"
  body:
    mode: json
    text: |
      {
        "name": "{{vars.name}}",
        "email": "{{vars.email}}"
      }
    form: []
  timeoutMs: 30000
  followRedirects: true
  validateSSL: true
\`\`\`

Then create a code cell that uses \`mutation()\` to create a \`MutationHandle\`. The \`onSettled\`
callback is called after each mutation completes (whether it succeeds or fails), and is the right
place to invalidate (refetch) related queries:

\`\`\`JavaScript cellId:kT7nL2yPcQjV8dFbHs6WgZeNiU0XoCpMtYlS4vJ1Aw5K9xR3m cellName:createUserMutation
return mutation({
    request: "createUser",
    onSettled: (data, error) => {
        usersList.refetch();
    }
});
\`\`\`

Then use \`mutateAsync(vars)\` to execute the mutation. The values are substituted into the
request page\\'s \`{{vars.X}}\` placeholders. Returns a \`Promise<QueryResponse>\` and throws
on error:

\`\`\`JavaScript cellId:V8dFbHs6WgZeNiU0XoCpMtYlS4vJ1Aw5K9xR3mkT7nL2yPcQj cellName:CreateUserButton
function CreateUserButton() {
    const [name, setName] = React.useState("");
    const [email, setEmail] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    return (
        <div className="flex gap-2">
            <input
                className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
            />
            <input
                className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
            />
            <button
                className="rounded bg-blue-600 px-2 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={async () => {
                    setLoading(true);
                    try {
                        await createUserMutation.mutateAsync({ name, email });
                    } finally {
                        setLoading(false);
                    }
                }}
                disabled={loading}
                type="button"
            >
                {loading ? "Creating..." : "Create User"}
            </button>
        </div>
    );
}
return CreateUserButton;
\`\`\`

\`\`\`JavaScript cellId:Hs6WgZeNiU0XoCpMtYlS4vJ1Aw5K9xR3mkT7nL2yPcQjV8dFb cellName:createUserButtonView
return render(() => <CreateUserButton />);
\`\`\`

#### Postgres mutations

For Postgres mutations, the same pattern applies — define \`{{vars.X}}\` placeholders in the SQL:

\`\`\`yaml
schemaVersion: 1
kind: Page
pageType: request
request:
  type: postgres
  connectionString: "{{env.DATABASE_URL}}"
  sql: |
    INSERT INTO users (name, email)
    VALUES (\\'{{vars.name}}\\', \\'{{vars.email}}\\')
    RETURNING id, name, email, created_at
  params: []
  timeoutMs: 30000
\`\`\`

\`\`\`JavaScript cellId:nL2yPcQjV8dFbHs6WgZeNiU0XoCpMtYlS4vJ1Aw5K9xR3mkT7 cellName:insertUserMutation
return mutation({
    request: "insertUser",
    onSettled: (data, error) => {
        usersList.refetch();
    }
});
\`\`\`

\`\`\`JavaScript cellId:PcQjV8dFbHs6WgZeNiU0XoCpMtYlS4vJ1Aw5K9xR3mkT7nL2y cellName:insertUserExample
await insertUserMutation.mutateAsync({ name: "Alice", email: "alice@example.com" });
\`\`\`

### IMPORTANT: Instructions for how to write JavaScript Code Cells

-   **IMPORTANT:** EVERY cell must \`return\` or \`yield\` a value.
-   **IMPORTANT:** EVERY cell must be formatted as a fenced code block with language \`JavaScript\`.
-   **IMPORTANT:** EVERY cell must have and a unique \`cellId\`, and a \`cellName\`, in the _info
    string_ of the fenced code block. That means that \`cellId\` and \`cellName\` must appear in the first line of the fenced code block.
-   **IMPORTANT:** \`cellName\` must be a unique, valid JavaScript identifier.

### IMPORTANT: Using the output of one cell in another cell

You can use the output of one cell in another cell by referencing the cell's \`cellId\`. For example,
here are two cells, one that produces an infinitely-increasing counter, and one that displays it as an HTML \`<h1>\`:

\`\`\`JavaScript cellId:if4ZSQvI8P1DBIqpm7mznjizJeM45Zlf5YNpWpOAxWOc9jDB cellName:count
let i = 0;
while (true) {
    yield i++;
    await new Promise((resolve) => setTimeout(resolve, 1000));
}
\`\`\`

\`\`\`JavaScript cellId:bVZVu194a34S63yjihuN5CtpafGzHt5GZmWH8Sa8ju9Kes0E cellName:CounterDisplay
function CounterDisplay() {
    return <h1>Counter: {count}</h1>;
}
return CounterDisplay;
\`\`\`

\`\`\`JavaScript cellId:m7mznjizJeM45Zlf5YNpWpOAxWOc9jDBif4ZSQvI8P1DBIqp cellName:counterView
return render(() => <CounterDisplay />);
\`\`\`

### IMPORTANT: Creating a \`request\` page

As previously mentioned, \`request\` pages are stored in \`.yml\` files and support HTTP, Postgres,
JavaScript, and gRPC requests.

**IMPORTANT:** HTTP request pages must set \`request.type: http\`.

**IMPORTANT:** Postgres request pages must set \`request.type: postgres\`.

**IMPORTANT:** gRPC request pages must set \`request.type: grpc\`.

**IMPORTANT:** For Postgres, always use an environment variable for the connection string, for
example \`{{env.DATABASE_URL}}\`. Do not inline credentials in the YAML.

**IMPORTANT:** Both \`query()\` and \`mutation()\` work with any Postgres, HTTP, or gRPC request page.
Use \`query()\` when you want caching and deduplication (typical for reads). Use \`mutation()\`
when each call should execute independently (typical for writes). For HTTP, any method works
with either API. For gRPC, any method works with either API.

**IMPORTANT:** Postgres \`NUMERIC\` / \`DECIMAL\` columns must be cast in your SQL to a type the
binary protocol can decode. Use \`::float8\` when you want a number, or \`::text\` when you need
the exact decimal string. For example: \`SELECT price::float8 FROM products\`. Without the cast,
NUMERIC values will appear as \`null\` in the response.

**IMPORTANT:** For Postgres, put the SQL in \`request.sql\`, use \`{{vars.NAME}}\` for caller-supplied
substitutions, and prefer \`timeoutMs: 30000\` unless the user asks otherwise.

**IMPORTANT:** For HTTP request pages, use the \`vars:\` Kv[] field to define default values for
\`{{vars.X}}\` template variables. Always include \`vars: []\` (empty list) even when there are no
variables, to make the field explicit.

**IMPORTANT:** When a request has a body (POST, PUT, PATCH), prefer \`mode: json\` over \`mode: text\`
for the body. Most APIs expect JSON, and using \`json\` mode ensures the \`Content-Type\` header is set
correctly. Only use \`mode: text\` if the API explicitly requires plain text.

Here is an example of a \`request\` page that fetches the last 100 pull requests from the
\`octocat/hello-world\` repository:

\`\`\`yaml
schemaVersion: 1
kind: Page
pageType: request
request:
  type: http
  method: GET
  url: https://api.github.com/repos/octocat/hello-world/pulls
  params:
    - enabled: true
      key: per_page
      value: "100"
    - enabled: true
      key: state
      value: all
  vars: []
  headers:
    - enabled: true
      key: Accept
      value: application/vnd.github.v3+json
  auth:
    type: none
  body:
    mode: none
    text: ""
    form: []
  timeoutMs: 30000
  followRedirects: true
  validateSSL: true
\`\`\`

Here is an example of a GET request with a path parameter injected via \`{{vars.X}}\` (call via
\`query({ request: "getPokemon", key: "getPokemon", vars: { name: "pikachu" } })\`):

\`\`\`yaml
schemaVersion: 1
kind: Page
pageType: request
request:
  type: http
  method: GET
  url: https://pokeapi.co/api/v2/pokemon/{{vars.name}}
  params: []
  vars:
    - enabled: true
      key: name
      value: pikachu
  headers: []
  auth:
    type: none
  body:
    mode: none
    text: ""
    form: []
  timeoutMs: 30000
  followRedirects: true
  validateSSL: true
\`\`\`

Here is an example of a POST request with a JSON body:

\`\`\`yaml
schemaVersion: 1
kind: Page
pageType: request
request:
  type: http
  method: POST
  url: https://api.example.com/items
  params: []
  vars: []
  headers:
    - enabled: true
      key: Accept
      value: application/json
  auth:
    type: none
  body:
    mode: json
    text: |
      {
        "name": "example",
        "value": 42
      }
    form: []
  timeoutMs: 30000
  followRedirects: true
  validateSSL: true
\`\`\`

Here is an example of a Postgres request page for reading data (typically called via
\`query({ request: "getUsers", key: "getUsers", vars: { workspaceId: "ws_123" } })\`):

\`\`\`yaml
schemaVersion: 1
kind: Page
pageType: request
request:
  type: postgres
  connectionString: "{{env.DATABASE_URL}}"
  sql: |
    SELECT id, email, created_at
    FROM users
    WHERE workspace_id = '{{vars.workspaceId}}'
    ORDER BY created_at DESC
    LIMIT 100
  params: []
  timeoutMs: 30000
\`\`\`

Here is an example of a Postgres request page for mutations (typically called via \`mutation()\`).
Use \`RETURNING\` to get the mutated rows back:

\`\`\`yaml
schemaVersion: 1
kind: Page
pageType: request
request:
  type: postgres
  connectionString: "{{env.DATABASE_URL}}"
  sql: |
    INSERT INTO users (name, email)
    VALUES (\\'{{vars.name}}\\', \\'{{vars.email}}\\')
    RETURNING id, name, email, created_at
  params: []
  timeoutMs: 30000
\`\`\`

Here is an example of a SQLite request page for querying a CSV file that has been added as an
attachment. When the \`filePath\` points to a \`.csv\` file, the CSV is automatically loaded into an
in-memory SQLite database as a table called \`data\`. You can then run any SQL query against it:

\`\`\`yaml
schemaVersion: 1
kind: Page
pageType: request
request:
  type: sqlite
  filePath: sales-data-abc123.csv
  sql: |
    SELECT region, SUM(revenue) as total_revenue
    FROM data
    GROUP BY region
    ORDER BY total_revenue DESC
  params: []
  timeoutMs: 30000
\`\`\`

Here is an example of a SQLite request page for querying a \`.sqlite\` database file. The file is
opened directly and you can query any table in it:

\`\`\`yaml
schemaVersion: 1
kind: Page
pageType: request
request:
  type: sqlite
  filePath: analytics-def456.sqlite
  sql: |
    SELECT name FROM sqlite_master WHERE type='table'
  params: []
  timeoutMs: 30000
\`\`\`

-   **IMPORTANT:** The \`filePath\` is just the filename of an attachment (e.g., \`data.csv\` or
    \`mydb.sqlite\`). The system resolves it from the document's \`.moment/attachments/\` directory
    automatically.
-   **IMPORTANT:** For CSV files, the table is always called \`data\`. For SQLite files, use
    \`SELECT name FROM sqlite_master WHERE type='table'\` to discover available tables.
-   **IMPORTANT:** SQLite request pages can be invoked from JavaScript code cells using \`query()\`
    just like HTTP and Postgres request pages.

Here is an example of a JavaScript request page. JavaScript request pages contain arbitrary
JavaScript code that runs as an async function. They are commonly used with AWS SDK v3 clients.
When used with AWS, the user must be logged in via the Moment AWS login flow (SSO). SigV4 signing
happens transparently — \`env._aws.credentials\` contains dummy values that are replaced at the
network layer by real credentials.

\`\`\`yaml
schemaVersion: 1
kind: Page
pageType: request
request:
  type: javascript
  code: |
    const { STSClient, GetCallerIdentityCommand } = await import("https://cdn.skypack.dev/@aws-sdk/client-sts@3.716.0");
    const client = new STSClient({ region: env._aws.region, credentials: env._aws.credentials });
    const identity = await client.send(new GetCallerIdentityCommand({}));
    return identity;
  params: []
  timeoutMs: 30000
\`\`\`

-   **IMPORTANT:** Always use \`env._aws.region\` and \`env._aws.credentials\` when constructing AWS
    SDK clients. Do NOT ask the user for AWS access keys or try to hardcode credentials.
-   **IMPORTANT:** If the user is not logged into AWS, the request will fail and the UI will prompt
    them to sign in via the Environments popover.
-   **IMPORTANT:** Import AWS SDK clients from \`https://cdn.skypack.dev/@aws-sdk/client-*@3.716.0\`.
    For example: \`client-s3\`, \`client-sts\`, \`client-lambda\`, \`client-dynamodb\`, \`client-ec2\`,
    \`client-ecs\`, \`client-cloudwatch\`, etc.
-   **IMPORTANT:** The \`code\` field contains JavaScript that runs as an async function with \`env\`
    as its argument. For AWS, use \`env._aws.region\` and \`env._aws.credentials\`. The code must
    \`return\` its result.
-   **IMPORTANT:** \`{{env.X}}\` variable substitution is NOT available in JavaScript request pages.
    For AWS, only \`env._aws\` is provided at runtime. However, \`{{params.X}}\` substitution IS
    supported — use \`request.params\` to define named parameters that are substituted into the code
    before execution. This is useful for parameterizing bucket names, table names, or other values
    that callers can override via \`query()\` or \`mutation()\`.

Here is an example of a gRPC request page that uses server reflection to call a unary RPC method.
gRPC request pages support both server reflection (\`useReflection: true\`) and explicit proto files
(\`protoFiles\` + \`importPaths\`). The \`service\` field should be the fully qualified service name
(e.g., \`package.ServiceName\`) and \`method\` should be the RPC method name.

\`\`\`yaml
schemaVersion: 1
kind: Page
pageType: request
request:
  type: grpc
  url: https://grpc.example.com:443
  protoFiles: []
  importPaths: []
  useReflection: true
  service: authzed.api.v1.SchemaService
  method: ReadSchema
  message: "{}"
  metadata: []
  auth:
    type: bearer
    token: "{{env.GRPC_TOKEN}}"
  tlsSkipVerify: false
  vars: []
  timeoutMs: 30000
\`\`\`

Here is a gRPC request page with \`{{vars.X}}\` substitution in the message body, and default
variable values defined in the \`vars:\` field (call via
\`query({ request: "readRelationships", key: "readRelationships", vars: { resourceType: "myapp/user" } })\`):

\`\`\`yaml
schemaVersion: 1
kind: Page
pageType: request
request:
  type: grpc
  url: https://grpc.example.com:443
  protoFiles: []
  importPaths: []
  useReflection: true
  service: authzed.api.v1.PermissionsService
  method: ReadRelationships
  message: |-
    {
      "consistency": { "fully_consistent": true },
      "relationship_filter": {
        "resource_type": "{{vars.resourceType}}"
      },
      "optional_limit": 100
    }
  metadata: []
  auth:
    type: bearer
    token: "{{env.AUTHZED_TOKEN}}"
  tlsSkipVerify: false
  vars:
    - enabled: false
      key: resourceType
      value: myapp/document
  timeoutMs: 30000
\`\`\`

-   **IMPORTANT:** gRPC request pages must set \`request.type: grpc\`.
-   **IMPORTANT:** Set \`useReflection: true\` when the server supports gRPC server reflection.
    Otherwise, provide proto file paths in \`protoFiles\` and their import paths in \`importPaths\`.
-   **IMPORTANT:** The \`message\` field is a JSON string representing the request message.
    Use \`{{vars.X}}\` for dynamic values that callers can override.
-   **IMPORTANT:** The \`metadata\` field is for gRPC metadata (headers), similar to HTTP headers.
    Use the same \`Kv[]\` format as HTTP headers.
-   **IMPORTANT:** Authentication works the same as HTTP — use \`auth.type: bearer\` with a token,
    or \`auth.type: none\` and set metadata entries directly. Use \`{{env.X}}\` for secrets.
-   **IMPORTANT:** Always include \`vars: []\` (empty list) even when there are no variables.
-   **IMPORTANT:** The gRPC response has \`kind: "grpc"\`. For unary calls, \`messages\` contains
    one entry. For streaming calls, \`messages\` contains all streamed responses. Access the parsed
    response body via \`messages[i].body\`.

For authentication, you can of course specify an \`Authorization\` header, and additionally you can
use env vars. **VERY IMPORTANT:** you will probably have to ask the user what environment variables
they have available! If they respond they have \`GITHUB_TOKEN\` available you can use it as
\`{{env.GITHUB_TOKEN}}\`. Observe how it appears in the \`Authorization\` header below:

\`\`\`yaml
schemaVersion: 1
kind: Page
pageType: request
request:
  type: http
  method: GET
  url: https://api.github.com/repos/moment-eng/moment/pulls
  params: []
  vars: []
  headers:
    - enabled: true
      key: Authorization
      value: Bearer {{env.GITHUB_TOKEN}}
  auth:
    type: none
  body:
    mode: none
    text: ""
    form: []
\`\`\`

Alternatively, you can use the \`auth\` field to specify the authentication type and credentials. For example:

\`\`\`yaml
schemaVersion: 1
kind: Page
pageType: request
request:
  type: http
  method: GET
  url: https://api.github.com/repos/moment-eng/moment/pulls
  params: []
  vars: []
  headers: []
  auth:
    type: bearer
    token: "{{env.GITHUB_TOKEN}}"
  body:
    mode: none
    text: ""
    form: []
\`\`\`

**IMPORTANT:** Kubernetes authentication is a very special case! You will want to set \`auth.type\` to
\`kubernetes\`, and **ALWAYS** use an environment variable for the context field, like
\`{{env.ACTIVE_KUBE_CONTEXT}}\`. The environment editor will automatically show a kubeconfig context
dropdown for this variable, so the user can select their context there. Remind the user to set the
context value in the environment selector in the top right.

\`\`\`yaml
schemaVersion: 1
kind: Page
pageType: request
request:
  type: http
  method: GET
  url: /api/v1/pods
  params: []
  vars: []
  headers: []
  auth:
    type: kubernetes
    context: "{{env.ACTIVE_KUBE_CONTEXT}}"
  body:
    mode: none
    text: ""
    form: []
\`\`\`

### IMPORTANT: Environments and switching credentials

-   Moment provides a way for users to define environments (*e.g.*, \`production\`, \`staging\`).
-   And environment is a collection of secrets and variables which can be
    accessed via mustache syntax inside \`request\` pages. Like: \`{{env.GITHUB_TOKEN}}\`.
-   **IMPORTANT:** You do not have the ability to know what tokens are exposed through these
    environments! If you want to use one in a \`request\` page, you'll need to ask the user to
    populate it in the environment selector in the top right, and to tell you the name of the
    variable to use.
-   **IMPORTANT:** Kubernetes context **MUST** always be set via an environment variable (e.g.,
    \`{{env.ACTIVE_KUBE_CONTEXT}}\`). The environment editor will automatically show a kubeconfig
    context dropdown for variables used this way, making it easy for the user to select their
    context. Remind the user to set the value in the environment selector in the top right.
-   **IMPORTANT:** If you create a new \`request\` page for a Kubernetes resource, **ALWAYS** check
    whether there are other \`request\` pages for Kubernetes resources, so that you can reuse the
    same environment variable name for the context (e.g., \`{{env.ACTIVE_KUBE_CONTEXT}}\`).

## Runtime Error Log

Code cells and request pages write errors to \`.moment/runtime-errors.log\` inside the document directory. This file is git-ignored and auto-truncated at 512 KB.

**After writing or modifying code cells or request pages, always check this log to verify execution succeeded.**

Each log entry includes:

-   \`[timestamp]\` — ISO 8601, use for checkpointing
-   \`[source]\` — \`code-cell\` or \`request-page\`
-   \`doc=<id>\` — document ID
-   \`page=<id>\` — page ID (if applicable)
-   \`cell=<id>\` — cell ID (code cell errors)
-   \`name=<name>\` — page name (request page errors)
-   Error message and stack trace (up to 10 lines)

Workflow:

1.  Write or modify a code cell or request page.
2.  Wait for execution.
3.  Read \`.moment/runtime-errors.log\` in the document directory.
4.  Look for entries timestamped after your change.
5.  If errors exist, fix the code and repeat.

## Diagnostics Snapshots

Code cell outputs and request page results are written to \`.moment/diagnostics/\` inside the document directory. Unlike the error log (which only captures errors), diagnostics snapshots capture **all** outputs — both successes and failures. Each file is **overwritten** on every execution, so it always reflects the current state.

### File Location

Each page gets its own snapshot file at:

\`\`\`
.moment/diagnostics/{pageId}/snapshot.log
\`\`\`

The snapshot file contains all code cell outputs and the request page result for that page combined into a single file. Find the \`pageId\` in \`moment.yml\` → \`pagesMetadata\` → \`id\`.

### Contents

Each snapshot file includes:

-   **Code Cell entries**: One section per cell with status (\`FULFILLED\`, \`REJECTED\`, \`ERROR\`, \`PENDING\`) and the cell's current output value or error
-   **Request Page entry**: Status (\`success\`, \`error\`), kind (\`http\`, \`postgres\`, \`javascript\`), and the response data or error details

### Workflow

After writing or modifying code cells or request pages:

1.  Wait for execution.
2.  Read \`.moment/diagnostics/{pageId}/snapshot.log\` for the page you modified.
3.  Check the output values and status of each cell or request.
4.  If errors exist, fix the code and repeat.

These files complement the runtime error log. Use \`.moment/runtime-errors.log\` for a chronological error history, and \`.moment/diagnostics/{pageId}/snapshot.log\` for the current state of all outputs on a page.

${MOMENT_MANAGED_SECTION_END}
</section>
`;
